<template>
  <div class="changer">
    <div class="changer__icon-box">
      <div class="selector selector-prev" @click="prevMouth(-1)"
           v-if="isActivePrev && indexMoth !== -1 && !savedPrevMonth"
           data-qa="month-changer-selector-prev">
      </div>
    </div>
    <p class="changer__current-month">{{currentMouth}}</p>
    <div class="changer__icon-box">
      <div class="selector selector-next"
           :class="{'selector-blinking' : hasUnfilledDays()}"
           @click="prevMouth(1)"
           v-if="isActiveNext && indexMoth !== 1 && !savedNextMonth"
           :title="hasUnfilledDays()"
           data-qa="month-changer-selector-next">
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import { useStore } from "vuex";
import { WTime, ShiftExistence } from '@/types';

const store = useStore()

const currentMouth = computed<string>(() => {
  return store.getters['getCurrentMoth']
})

const currentDate = computed<Date>(() => {
  return store.getters['getCurrentDate']
})

// Наличие смен и ФРВ в следующем месяце
const isShiftExistence = computed<ShiftExistence>(() => {
  return store.getters["data/shiftExistence"] && store.getters["data/shiftExistence"].frv;
});

const hasUnsavedChanges = computed(() => {
  return store.getters['hasUnsavedChanges']
})

const timeScheduleDetailsPast = computed<WTime[]>(() => {
  return store.getters['data/timeScheduleDetailsPast']
})

const savedPrevMonth = computed<null | string>(() => {
  return localStorage.getItem('activePrevMonth')
})

const savedNextMonth = computed<null | string>(() => {
  return localStorage.getItem('activeNextMonth')
})

const currentMonthNumber: number = new Date().getMonth();

const isActivePrev = computed<boolean>(() => {
  const currentDateMonth: number = currentDate.value.getMonth();
  // Вычисляем предыдущий месяц для корректного перехода между годами
  const prevMonth = (currentMonthNumber - 1 + 12) % 12;
  return prevMonth !== currentDateMonth;
});

const isActiveNext = computed<boolean>(() => {
  const currentDateMonth: number = currentDate.value.getMonth();
  // Вычисляем следующий месяц для корректного перехода между годами
  const nextMonth = (currentMonthNumber + 1) % 12;
  return nextMonth !== currentDateMonth;
});

const indexMoth = ref<number>(0)

const saveSelectedMonth = () => {
  if (!isActiveNext.value) {
    localStorage.setItem("activeNextMonth", '1')
  } else if (!isActivePrev.value) {
    localStorage.setItem("activePrevMonth", '1')
  } else {
    localStorage.removeItem("activeNextMonth")
    localStorage.removeItem("activePrevMonth")
  }
}

const prevMouth = async (index: number) => {
  if (hasUnsavedChanges.value) {
    // Если выполняется переход на другую аптеку и добавлен новый внешний сотрудник - запрашиваем подтверждение
    const leaveConfirm = window.confirm('Есть несохраненные изменения на графике, перейти на другой месяц?')

    if (leaveConfirm) {
      store.commit('setUnsavedChanges', false)
    } else {
      return false
    }
  }

  store.commit('user/setLoader', true)
  indexMoth.value += index
  store.commit('setMoth', index)
  store.commit('data/removeOldShift')
  store.commit('setDate', currentDate.value)
  store.commit('data/clearOpenedCreateShift')
  await store.dispatch("data/getExternalUsers");
  await store.dispatch('data/getFondWorkTime')
  await store.dispatch("data/getHolidays");
  await store.dispatch("data/getShiftsExistence");
  await store.dispatch("data/getTimeScheduleDetails");
  await store.dispatch("data/getPastTimeScheduleDetails");
  await store.dispatch('data/getTimeSchedule')
  await store.dispatch('data/getPharmSchedule')
  await store.dispatch('data/getAdminShift', true);
  await store.dispatch('data/getFondWorkTimeHuman')
  saveSelectedMonth()
  store.commit('user/setLoader', false)
}

const isOfflineDirectorRole = computed<boolean>(() => {
  return store.getters['getOfflineDirectorRole']
})

const isWFMReaderRole = computed<boolean>(() => {
  return store.getters['getWFMReaderRole']
})

// Проверка на наличие "more" или "less" в любом дне следующего месяца
const containsDayFullMoreOrLess = (obj: object) => {
  for (const date in obj) {
    if (obj[date].dayFull === 'more' || obj[date].dayFull === 'less') {
      return true
    }
  }
  return false
}

const hasUnfilledDays = () => {
  if (isOfflineDirectorRole.value || isWFMReaderRole.value) {
    return undefined
  }

  if (isShiftExistence.value && containsDayFullMoreOrLess(timeScheduleDetailsPast.value)) {
    return 'Необходимо исправить время в следующем месяце'
  }
  return undefined
}
</script>

<script lang="ts">
export default {
  name: 'vita-month-changer'
}
</script>

<style scoped lang="scss">
@use '../../static/style/variables.scss' as *;
@use '../../static/style/mixins.scss' as *;

.selector {
  width: 20px;
  height: 20px;
  position: relative;
  cursor: pointer;

  &-next::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjEiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEwLjUwMjYgNy41OTMwMkwxMi45MDk3IDEwLjAwMDFMMTAuNTAyNiAxMi40MDcxTDEwLjAzMyAxMS45Mzc2TDExLjYzNzIgMTAuMzMzNEg3LjkxOTI3VjkuNjY2NzVIMTEuNjM3MkwxMC4wMzMgOC4wNjI1OEwxMC41MDI2IDcuNTkzMDJaTTEwLjUwNTQgMi44MzM0MUM5LjUyMzgzIDIuODMzNDEgOC41OTczNyAzLjAxOTggNy43MjYwMiAzLjM5MjU2QzYuODU0NjcgMy43NjUzNCA2LjA5MTcyIDQuMjc4NjggNS40MzcxNyA0LjkzMjZDNC43ODI2MiA1LjU4NjUyIDQuMjY4NzggNi4zNDg3MiAzLjg5NTY1IDcuMjE5MjFDMy41MjI1MSA4LjA4OTcxIDMuMzM1OTQgOS4wMTU3MyAzLjMzNTk0IDkuOTk3MjlDMy4zMzU5NCAxMC45OTI3IDMuNTIyMzIgMTEuOTIyNyAzLjg5NTA4IDEyLjc4NzFDNC4yNjc4NiAxMy42NTE1IDQuNzgxMjEgMTQuNDExIDUuNDM1MTMgMTUuMDY1NUM2LjA4OTA0IDE1LjcyMDEgNi44NTEyNCAxNi4yMzM5IDcuNzIxNzMgMTYuNjA3QzguNTkyMjMgMTYuOTgwMiA5LjUxODI2IDE3LjE2NjcgMTAuNDk5OCAxNy4xNjY3QzExLjQ5NTMgMTcuMTY2NyAxMi40MjUyIDE2Ljk4MDQgMTMuMjg5NiAxNi42MDc2QzE0LjE1NCAxNi4yMzQ4IDE0LjkxMzUgMTUuNzIxNSAxNS41NjggMTUuMDY3NkMxNi4yMjI2IDE0LjQxMzYgMTYuNzM2NCAxMy42NTQ5IDE3LjEwOTYgMTIuNzkxNEMxNy40ODI3IDExLjkyNzggMTcuNjY5MyAxMC45OTgzIDE3LjY2OTMgMTAuMDAyOUMxNy42NjkzIDkuMDIxMyAxNy40ODI5IDguMDk0ODQgMTcuMTEwMSA3LjIyMzVDMTYuNzM3MyA2LjM1MjE1IDE2LjIyNCA1LjU4OTIgMTUuNTcwMSA0LjkzNDY0QzE0LjkxNjIgNC4yODAxIDE0LjE1NzQgMy43NjYyNiAxMy4yOTM5IDMuMzkzMTJDMTIuNDMwMyAzLjAxOTk4IDExLjUwMDggMi44MzM0MSAxMC41MDU0IDIuODMzNDFaTTEwLjUwMjYgMy41MDAwOEMxMi4zMDgyIDMuNTAwMDggMTMuODQyOSA0LjEzMjAzIDE1LjEwNjggNS4zOTU5MUMxNi4zNzA3IDYuNjU5OCAxNy4wMDI2IDguMTk0NTMgMTcuMDAyNiAxMC4wMDAxQzE3LjAwMjYgMTEuODA1NiAxNi4zNzA3IDEzLjM0MDQgMTUuMTA2OCAxNC42MDQyQzEzLjg0MjkgMTUuODY4MSAxMi4zMDgyIDE2LjUwMDEgMTAuNTAyNiAxNi41MDAxQzguNjk3MDUgMTYuNTAwMSA3LjE2MjMzIDE1Ljg2ODEgNS44OTg0NCAxNC42MDQyQzQuNjM0NTUgMTMuMzQwNCA0LjAwMjYgMTEuODA1NiA0LjAwMjYgMTAuMDAwMUM0LjAwMjYgOC4xOTQ1MyA0LjYzNDU1IDYuNjU5OCA1Ljg5ODQ0IDUuMzk1OTFDNy4xNjIzMyA0LjEzMjAzIDguNjk3MDUgMy41MDAwOCAxMC41MDI2IDMuNTAwMDhaIiBmaWxsPSIjODg4ODg4Ii8+Cjwvc3ZnPgo=");
  }

  &-prev::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjEiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEwLjQ5NzQgNy41OTMwMkw4LjA5MDMzIDEwLjAwMDFMMTAuNDk3NCAxMi40MDcxTDEwLjk2NyAxMS45Mzc2TDkuMzYyNzkgMTAuMzMzNEgxMy4wODA3VjkuNjY2NzVIOS4zNjI3OUwxMC45NjcgOC4wNjI1OEwxMC40OTc0IDcuNTkzMDJaTTEwLjQ5NDYgMi44MzM0MUMxMS40NzYyIDIuODMzNDEgMTIuNDAyNiAzLjAxOTggMTMuMjc0IDMuMzkyNTZDMTQuMTQ1MyAzLjc2NTM0IDE0LjkwODMgNC4yNzg2OCAxNS41NjI4IDQuOTMyNkMxNi4yMTc0IDUuNTg2NTIgMTYuNzMxMiA2LjM0ODcyIDE3LjEwNDQgNy4yMTkyMUMxNy40Nzc1IDguMDg5NzEgMTcuNjY0MSA5LjAxNTczIDE3LjY2NDEgOS45OTcyOUMxNy42NjQxIDEwLjk5MjcgMTcuNDc3NyAxMS45MjI3IDE3LjEwNDkgMTIuNzg3MUMxNi43MzIxIDEzLjY1MTUgMTYuMjE4OCAxNC40MTEgMTUuNTY0OSAxNS4wNjU1QzE0LjkxMSAxNS43MjAxIDE0LjE0ODggMTYuMjMzOSAxMy4yNzgzIDE2LjYwN0MxMi40MDc4IDE2Ljk4MDIgMTEuNDgxNyAxNy4xNjY3IDEwLjUwMDIgMTcuMTY2N0M5LjUwNDczIDE3LjE2NjcgOC41NzQ4IDE2Ljk4MDQgNy43MTA0IDE2LjYwNzZDNi44NDU5OSAxNi4yMzQ4IDYuMDg2NTEgMTUuNzIxNSA1LjQzMTk2IDE1LjA2NzZDNC43Nzc0MiAxNC40MTM2IDQuMjYzNTggMTMuNjU0OSAzLjg5MDQ0IDEyLjc5MTRDMy41MTczIDExLjkyNzggMy4zMzA3MyAxMC45OTgzIDMuMzMwNzMgMTAuMDAyOUMzLjMzMDczIDkuMDIxMyAzLjUxNzExIDguMDk0ODQgMy44ODk4NyA3LjIyMzVDNC4yNjI2NSA2LjM1MjE1IDQuNzc2IDUuNTg5MiA1LjQyOTkyIDQuOTM0NjRDNi4wODM4MyA0LjI4MDEgNi44NDI1NiAzLjc2NjI2IDcuNzA2MSAzLjM5MzEyQzguNTY5NjYgMy4wMTk5OCA5LjQ5OTE2IDIuODMzNDEgMTAuNDk0NiAyLjgzMzQxWk0xMC40OTc0IDMuNTAwMDhDOC42OTE4NCAzLjUwMDA4IDcuMTU3MTIgNC4xMzIwMyA1Ljg5MzIzIDUuMzk1OTFDNC42MjkzNCA2LjY1OTggMy45OTc0IDguMTk0NTMgMy45OTc0IDEwLjAwMDFDMy45OTc0IDExLjgwNTYgNC42MjkzNCAxMy4zNDA0IDUuODkzMjMgMTQuNjA0MkM3LjE1NzEyIDE1Ljg2ODEgOC42OTE4NCAxNi41MDAxIDEwLjQ5NzQgMTYuNTAwMUMxMi4zMDMgMTYuNTAwMSAxMy44Mzc3IDE1Ljg2ODEgMTUuMTAxNiAxNC42MDQyQzE2LjM2NTUgMTMuMzQwNCAxNi45OTc0IDExLjgwNTYgMTYuOTk3NCAxMC4wMDAxQzE2Ljk5NzQgOC4xOTQ1MyAxNi4zNjU1IDYuNjU5OCAxNS4xMDE2IDUuMzk1OTFDMTMuODM3NyA0LjEzMjAzIDEyLjMwMyAzLjUwMDA4IDEwLjQ5NzQgMy41MDAwOFoiIGZpbGw9IiM4ODg4ODgiLz4KPC9zdmc+Cg==");
  }
}

.selector-blinking {
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 2;
    animation: blink 1.5s infinite;
    animation-fill-mode: both;
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjEiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEwLjUwNTQgMi44MzM0MUM5LjUyMzgzIDIuODMzNDEgOC41OTczNyAzLjAxOTggNy43MjYwMiAzLjM5MjU2QzYuODU0NjcgMy43NjUzNCA2LjA5MTcyIDQuMjc4NjkgNS40MzcxNyA0LjkzMjZDNC43ODI2MiA1LjU4NjUyIDQuMjY4NzggNi4zNDg3MiAzLjg5NTY1IDcuMjE5MjFDMy41MjI1MSA4LjA4OTcxIDMuMzM1OTQgOS4wMTU3MyAzLjMzNTk0IDkuOTk3MjlDMy4zMzU5NCAxMC45OTI3IDMuNTIyMzIgMTEuOTIyNyAzLjg5NTA4IDEyLjc4NzFDNC4yNjc4NiAxMy42NTE1IDQuNzgxMjEgMTQuNDExIDUuNDM1MTMgMTUuMDY1NUM2LjA4OTA0IDE1LjcyMDEgNi44NTEyNCAxNi4yMzM5IDcuNzIxNzMgMTYuNjA3QzguNTkyMjMgMTYuOTgwMiA5LjUxODI2IDE3LjE2NjcgMTAuNDk5OCAxNy4xNjY3QzExLjQ5NTMgMTcuMTY2NyAxMi40MjUyIDE2Ljk4MDQgMTMuMjg5NiAxNi42MDc2QzE0LjE1NCAxNi4yMzQ4IDE0LjkxMzUgMTUuNzIxNSAxNS41NjggMTUuMDY3NkMxNi4yMjI2IDE0LjQxMzYgMTYuNzM2NCAxMy42NTQ5IDE3LjEwOTYgMTIuNzkxNEMxNy40ODI3IDExLjkyNzggMTcuNjY5MyAxMC45OTgzIDE3LjY2OTMgMTAuMDAyOUMxNy42NjkzIDkuMDIxMyAxNy40ODI5IDguMDk0ODQgMTcuMTEwMSA3LjIyMzVDMTYuNzM3MyA2LjM1MjE1IDE2LjIyNCA1LjU4OTIgMTUuNTcwMSA0LjkzNDY0QzE0LjkxNjIgNC4yODAxIDE0LjE1NzQgMy43NjYyNiAxMy4yOTM5IDMuMzkzMTJDMTIuNDMwMyAzLjAxOTk4IDExLjUwMDggMi44MzM0MSAxMC41MDU0IDIuODMzNDFaIiBmaWxsPSIjRkYzQjMwIi8+CjxwYXRoIGQ9Ik0xMi45MDk4IDEwTDEwLjUwMjggNy41OTI5OEwxMC4wMzMyIDguMDYyNTRMMTEuNjM3NCA5LjY2NjcxSDcuOTE5NDNWMTAuMzMzNEgxMS42Mzc0TDEwLjAzMzIgMTEuOTM3NUwxMC41MDI4IDEyLjQwNzFMMTIuOTA5OCAxMFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=");
  }
}

@keyframes blink {
  0% { opacity: 0 }
  50% { opacity: 1 }
  70% { opacity: 1 }
  100% { opacity: 0 }
}

.changer {
  display: flex;
  justify-content: center;
  padding: 5px 0;
  height: 30px;
  background: #CCCCCC;
  margin-bottom: 4px;
  user-select: none;
  border-radius: 0 3px 3px 0;

  .rotate {
    transform: rotate(180deg);
  }

  &__icon {
    cursor: pointer;
  }

  &__current-month {
    font-weight: 500;
    font-size: 16px;
    line-height: 1.2;
    margin: 0 20px;
    min-width: 75px;
    text-align: center;
    text-transform: capitalize;
  }
}

.changer__icon-box {
  width: 20px;
}

</style>
